<template>
  <div class="rental">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #h__item.name><div class="table-default__left">Item</div></template>
        <template #item.name="{ row, index }">
          <div
            v-if="status === 'completed' || (status !== 'completed' && index !== table.items.length)"
            class="table-default__left"
          >
            {{ row.item.name }}
          </div>
          <app-select
            v-if="status !== 'completed' && index === table.items.length"
            @option:selected="onNewProductChange"
            v-model="new_row"
            :options="purchaseitem_options"
            :clearable="false"
            :calculate-position="withPopper"
            append-to-body
            placeholder="Enter purchase item name"
            class="select--300"
            ref="new_product"
            :tabindex="1"
          />
        </template>
        <template #price="{ row, index }">
          <template v-if="status !== 'completed'">
            <template v-if="index !== table.items.length">{{ row.price.toLocaleString() }}$</template>
            <template v-if="index === table.items.length && new_row">{{ new_row.price.toLocaleString() }}$</template>
          </template>
          <span v-else>{{ row.price.toLocaleString() }}$</span>
        </template>
        <template #stock="{ row, index }">
          <template v-if="status !== 'completed'">
            <template v-if="index !== table.items.length">{{ row.stock }}</template>
            <template v-if="index === table.items.length && new_row">{{ new_row.balance }}</template>
          </template>
          <span v-else>{{ row.stock }}</span>
        </template>
        <template #quantity="{ row, index }">
          <template v-if="status !== 'completed'">
            <app-cells position="center" :indent="false">
              <input
                v-if="index !== table.items.length"
                v-model.number="row.quantity"
                class="input input--300"
                :class="{'input--error': row.quantity > row.stock}"
                @change="onChangeTableInput(row.id, row.quantity)"
                style="text-align: center"
              />
              <input
                v-if="index === table.items.length && new_row"
                v-model.number="new_row.quantity"
                class="input input--300"
                :class="{'input--error': new_row.quantity > new_row.balance}"
                @keydown.enter="onNewRowSubmit"
                style="text-align: center"
                ref="new_qty"
                :tabindex="2"
              />
            </app-cells>
          </template>
          <span v-else>{{ row.quantity }}</span>
        </template>
        <template #amount="{ row, index }">
          <template v-if="status !== 'completed'">
            <template v-if="index !== table.items.length">{{ row.amount }}$</template>
          </template>
          <span v-else>{{ row.amount }}$</span>
        </template>
        <template #controls="{ row, index }">
          <div v-if="index !== table.items.length" class="table-default__controls">
            <button @click="onDeletePurchaseItem(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
    </template>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete purchase
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this purchase?</div>
          <!--<div v-if="this.current_domain" class="popup__info">{{ this.current_domain.name }}</div>-->
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteItem" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deletePurchaseItem,
  getEquipmentStoragePurchaseItems,
  getPurchaseItems,
  patchPurchaseItem,
  postPurchaseItems,
} from '@/http'
import { createPopper } from '@popperjs/core'

export default {
  name: 'Purchase',
  props: {
    status: {
      type: String
    }
  },
  data() {
    return {
      is_loaded: false,
      table: {
        items: [],
        columns: [],
        options: {
          headings: {},
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      columns_draft: ['item.name', 'price', 'stock', 'quantity', 'amount', 'controls'],
      headings_draft: {
        'item.name': 'Item',
        'price': 'Price',
        'stock': 'Stock',
        'quantity': 'Quantity',
        'amount': 'Amount',
        'controls': ''
      },
      columns_in_process: ['item.name', 'price', 'stock', 'quantity', 'amount', 'controls'],
      headings_in_process: {
        'item.name': 'Item',
        'price': 'Price',
        'stock': 'Stock',
        'quantity': 'Quantity',
        'amount': 'Amount',
        'controls': ''
      },
      current_id: null,
      new_row: null,
      placement: 'bottom',
      purchaseitem_options: [],
    }
  },
  created() {
    this.fetchPurchaseItems()
    getEquipmentStoragePurchaseItems()
      .then(response => {
        this.purchaseitem_options = response.data
      })
  },
  watch: {
    status(value) {
      this.switchTable(value)
    }
  },
  methods: {
    onNewRowSubmit() {
      postPurchaseItems(this.$route.params.id, { item: this.new_row.id, quantity: this.new_row.quantity })
        .then(res => {
          this.table.items = res.data
          this.table.items.push({})
          this.new_row = null
          this.$nextTick(() => {
            this.$refs.new_product.$el.querySelector('input').focus()
          })
          this.$notify({
            type: 'success',
            title: 'Purchase item added',
          })
          this.$emit('getRentalInfo')
        })
    },
    onNewProductChange () {
      this.$nextTick(() => { this.$refs.new_qty.focus() })
    },
    onChangeTableInput(id, quantity) {
      patchPurchaseItem(this.$route.params.id, id, { quantity })
        .then(() => {
          this.$emit('getRentalInfo')
          this.fetchPurchaseItems()
        })
    },
    fetchPurchaseItems() {
      getPurchaseItems(this.$route.params.id).then(res => {
        this.is_loaded = true
        this.table.items = res.data
        if (this.status !== 'completed') this.table.items.push({})
        this.switchTable(this.status)
      })
    },
    onDeletePurchaseItem(id) {
      this.$modal.show('delete-modal')
      this.current_id = id
    },
    switchTable(status) {
      if (status === 'draft') {
        this.table.columns = this.columns_draft
        this.table.options.headings = this.headings_draft
      } else if (status === 'in_process' || status === 'completed') {
        this.table.columns = this.columns_in_process
        this.table.options.headings = this.headings_in_process
        if (status === 'completed') {
          this.table.columns.pop()
          if (!this.table.items.length) return
          if (!this.table.items[this.table.items.length - 1].id) this.table.items.pop()
        }
      }
    },
    onDeleteItem() {
      this.$refs.delete.preload = true
      deletePurchaseItem(this.$route.params.id, this.current_id)
        .finally(() => { this.$refs.delete.preload = false })
        .then(res => {
          this.table.items = res.data
          if (this.status === 'draft') this.table.items.push({})
          this.$notify({
            type: 'success',
            title: 'Purchase item deleted',
          })
          this.$emit('getRentalInfo')
          this.$modal.hide('delete-modal')
        })
    },
    withPopper (dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
              adaptive: true
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          },
        ]
      })
    }
  }
}
</script>
<style lang="sass" scoped>
.rental
  position: relative
  min-height: 100px
</style>
